import { create } from 'zustand';
import { formatDuration, isAtCheckpoint } from '../utils/api';
import recordBoatData from '../data/recordBoatData';

/**
 * Race tracking application central state store
 * Manages all data related to the race, boats and checkpoints
 */
const useRaceStore = create((set, get) => ({
  // API related state
  apiStatus: 'connecting', // connecting, connected, timeout, error
  lastUpdate: null,
  
  // Race data
  raceInfo: null,
  currentBoat: null,
  recordBoat: recordBoatData, // Static record boat data
  
  // Track history
  trackHistory: [],
  checkpointsReached: [],
  
  // Fenerbahçe Doğuş etap bilgileri
  fbDogusStages: [],
  fbDogusPosition: [0, 0],
  fbDogusHeading: 0,
  fbDogusVMG: 5.66, // Sabit VMG değeri - kullanıcının verdiği değer
  
  // Current stage info
  currentStage: 1,
  stageProgress: 0,
  overallProgress: 0,
  
  // Comparison metrics
  comparison: {
    speedDifference: 0,
    timeDifference: 0,
    stageVmgDifference: 0,
    advantageHours: 0
  },
  
  // Initialize data from API
  initialize: async () => {
    set({ apiStatus: 'connecting' });
    
    // Initial data fetch - önbellekten değil API'den taze veri al
    await get().fetchData(true); // force=true ile her zaman API'den taze veri al
  },
  
  // Fetch data from API - tamamen yeni yaklaşım
  fetchData: async (force = false) => {
    try {
      // API durumunu güncelle
      set({ apiStatus: 'connecting' });
      
      // Doğrudan API'ye istek yap - CORS proxy kullanmadan
      const timestamp = new Date().getTime();
      const randomStr = Math.random().toString(36).substring(7);
      const apiUrl = `http://mavivatan.imarine.com.tr:3583/api/v1/race?nocache=${timestamp}&r=${randomStr}`;
      
      console.log('API isteği yapılıyor (doğrudan):', apiUrl);
      
      // Fetch API ile istek yap - önbellekleme olmadan
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        cache: 'no-store' // Fetch API önbellekleme kontrolü
      });
      
      if (!response.ok) {
        throw new Error(`API isteği başarısız: ${response.status}`);
      }
      
      // JSON yanıtı al
      const data = await response.json();
      console.log('API yanıtı:', data);
      
      // API yanıtının doğru yapıda olup olmadığını kontrol et
      if (!data || !data.race || !data.tracks || !data.tracks.length) {
        throw new Error('API yanıtı geçerli veri içermiyor');
      }
      
      if (data) {
        const { race, tracks } = data;
        const currentTrack = tracks[0];
        const now = new Date();
        
        // Temel verileri güncelle
        set({ 
          apiStatus: 'connected',
          lastUpdate: now,
          raceInfo: race,
          currentBoat: currentTrack
        });
        
        // Tarihçeye ekle
        set(state => ({ 
          trackHistory: [...state.trackHistory, currentTrack]
        }));
        
        // Konum ve karşılaştırma verilerini işle
        get().processPosition(currentTrack);
        get().calculateComparison();
        
        console.log('Veri başarıyla güncellendi:', {
          zaman: now.toLocaleTimeString(),
          konum: [currentTrack.lat, currentTrack.lon],
          hız: currentTrack.sog_kn,
          yön: currentTrack.cog_deg
        });
      } else {
        throw new Error('API yanıtında veri yok');
      }
    } catch (error) {
      console.error('API veri alma hatası:', error);
      console.error('Hata detayları:', {
        mesaj: error.message,
        kod: error.code,
        url: error.url,
        stack: error.stack
      });
      
      // Hata durumunu güncelle
      set({ 
        apiStatus: 'error',
        lastError: error.message
      });
      
      // Mock veri kullanmayı dene - gerçek API başarısız olduğunda
      try {
        console.log('API hatası nedeniyle mock veri kullanılıyor...');
        const mockData = require('../data/mockData').mockApiResponse;
        
        if (mockData && mockData.race && mockData.tracks && mockData.tracks.length > 0) {
          const { race, tracks } = mockData;
          const currentTrack = tracks[0];
          
          // Mock veri ile güncelle
          set({ 
            apiStatus: 'connected_fallback',
            lastUpdate: new Date(),
            raceInfo: race,
            currentBoat: currentTrack
          });
          
          // Konum ve karşılaştırma verilerini işle
          get().processPosition(currentTrack);
          get().calculateComparison();
          
          console.log('Mock veri başarıyla yüklendi');
        }
      } catch (mockError) {
        console.error('Mock veri yükleme hatası:', mockError);
      }
    }
  },
  
  // Process new position data
  processPosition: (track) => {
    const { recordBoat, currentStage, checkpointsReached } = get();
    
    if (!track) return;
    
    // Calculate overall progress
    const totalDistance = recordBoat.totalDistanceNM;
    const distanceCovered = track.distance_m / 1852; // meters to NM
    const overallProgress = Math.min(1, distanceCovered / totalDistance);
    
    // Calculate stage progress - only when needed
    const stageProgress = calculateStageProgress(track, currentStage);
    
    // Linea Rossa'nın VMG değerini hesapla
    const lineaRossaVMG = calculateLineaRossaVMG(track);
    
    // Performans için etap bilgilerini daha az sıklıkla güncelle
    // Her 10 pozisyon güncellemesinde bir kez çalıştır
    let updatedStages = get().lineaRossaStages;
    const trackHistoryLength = get().trackHistory.length;
    if (trackHistoryLength % 10 === 0) {
      updatedStages = updateLineaRossaStages(track);
    }
    
    set({ 
      overallProgress,
      stageProgress,
      lineaRossaVMG,
      lineaRossaStages: updatedStages
    });
    
    const position = [track.lat, track.lon];
    
    // Check if we've reached any new checkpoints - optimize with early returns
    // Only check checkpoints if we're close to one
    const shouldCheckCheckpoints = recordBoat.checkpoints.some(cp => {
      // Basit mesafe kontrolü - tam hesaplamadan daha hızlı
      const latDiff = Math.abs(position[0] - cp.position[0]);
      const lonDiff = Math.abs(position[1] - cp.position[1]);
      return latDiff < 0.1 && lonDiff < 0.1; // ~10km içindeyse kontrol et
    });
    
    if (shouldCheckCheckpoints) {
      for (const checkpoint of recordBoat.checkpoints) {
        if (
          !checkpointsReached.includes(checkpoint.id) && 
          isAtCheckpoint(position, checkpoint)
        ) {
          // Reached a new checkpoint
          set(state => ({ 
            checkpointsReached: [...state.checkpointsReached, checkpoint.id]
          }));
          
          // Determine current stage based on checkpoints reached
          const newStage = Math.min(
            checkpointsReached.length, 
            recordBoat.stages.length
          );
          
          if (newStage !== get().currentStage) {
            set({ currentStage: newStage });
          }
          
          break;
        }
      }
    }
    
    // Update progress - only set once to reduce renders
    set({ 
      stageProgress,
      overallProgress: track.completed
    });
  },
  
  // Calculate comparison metrics against record boat
  calculateComparison: () => {
    const { recordBoat, currentBoat, currentStage, raceInfo, lineaRossaVMG } = get();
    
    if (!currentBoat || !raceInfo) return;
    
    // Get the record stage data
    const recordStageData = recordBoat.stages[currentStage - 1];
    
    // Calculate speed difference
    const speedDifference = currentBoat.sog_kn - recordStageData.vmg;
    
    // Linea Rossa'nın VMG değerini kullan
    const currentVmg = lineaRossaVMG > 0 ? lineaRossaVMG : currentBoat.sog_kn;
    const stageVmgDifference = currentVmg - recordStageData.vmg;
    
    // Calculate time difference
    // This is simplified - in a real app would be more sophisticated
    const recordTimeToThisPoint = calculateRecordTimeToCurrentPoint(
      currentBoat.completed, 
      recordBoat
    );
    const currentTimeElapsed = raceInfo.duration_sec / 3600; // hours
    const timeDifference = recordTimeToThisPoint - currentTimeElapsed;
    
    // Update the comparison metrics
    set({
      comparison: {
        speedDifference,
        timeDifference,
        stageVmgDifference,
        advantageHours: timeDifference
      }
    });
  },
  
  // Formatted data for UI
  getFormattedData: () => {
    const { raceInfo, currentBoat, comparison, lastUpdate, apiStatus, recordBoat } = get();
    
    if (!raceInfo || !currentBoat) {
      return {
        isLoading: true,
        error: apiStatus === 'error' ? 'Veri yüklenirken hata oluştu' : null
      };
    }
    
    // İlerleme hesaplaması için tüm etapların mesafelerini topla
    const totalRaceDistance = recordBoat.stages.reduce((total, stage) => total + stage.distance_nm, 0);
    
    // Çanakkale Çıkışı koordinatları
    const canakkaleExit = [40.04150, 26.19118]; // Çanakkale Boğazı Çıkışı koordinatları
    
    // Çanakkale Çıkışı'ndan güncel konuma olan mesafeyi hesapla (NM)
    const distanceFromCanakkale = calculateDistance(
      [currentBoat.lat, currentBoat.lon],
      canakkaleExit
    );
    
    // İlk 3 etabın toplam mesafesini hesapla
    const etap1Distance = recordBoat.stages[0].distance_nm; // Hopa-Sinop
    const etap2Distance = recordBoat.stages[1].distance_nm; // Sinop-Boğaz Girişi
    const etap3Distance = recordBoat.stages[2].distance_nm; // Boğaz Girişi-Çanakkale Çıkışı
    
    // İlk 3 etabın toplam mesafesi
    const firstThreeStagesDistance = etap1Distance + etap2Distance + etap3Distance;
    
    // Toplam kat edilen mesafe: İlk 3 etap toplamı - Çanakkale'den güncel konuma mesafe
    // Bu yöntem daha iyi bir deniz rotası tahminini sağlar
    const totalDistanceCovered = firstThreeStagesDistance - distanceFromCanakkale;
    
    // Doğru ilerleme yüzdesi hesaplaması (0-100 arası)
    const progress = Math.min(100, (totalDistanceCovered / totalRaceDistance) * 100);
    
    // API'den gelen mesafe verileri (NM)
    const distanceCovered = currentBoat.distance_m / 1852; // meters to NM
    const distanceRemaining = raceInfo.totalDistance_NM - distanceCovered;
    
    // Calculate ETA based on current average speed
    const hoursRemaining = currentBoat.asog_kn > 0 
      ? distanceRemaining / currentBoat.asog_kn 
      : 0;
      
    const etaHours = Math.floor(hoursRemaining);
    const etaMinutes = Math.floor((hoursRemaining - etaHours) * 60);
    
    return {
      isLoading: false,
      apiStatus,
      lastUpdate: lastUpdate ? lastUpdate.toLocaleTimeString() : '',
      raceTitle: raceInfo.title,
      currentSpeed: currentBoat.sog_kn,
      averageSpeed: currentBoat.asog_kn,
      course: currentBoat.cog_deg,
      position: [currentBoat.lat, currentBoat.lon],
      progress: progress, // Etap mesafelerine dayalı hesaplanan ilerleme
      elapsedTime: formatDuration(raceInfo.duration_sec),
      distanceCovered: distanceCovered.toFixed(1),
      distanceRemaining: distanceRemaining.toFixed(1),
      eta: `${etaHours}h ${etaMinutes}m`,
      comparison: {
        speedDifference: comparison.speedDifference.toFixed(2),
        timeDifference: comparison.timeDifference.toFixed(1),
        stageVmgDifference: comparison.stageVmgDifference.toFixed(2),
        advantageHours: comparison.advantageHours.toFixed(1)
      }
    };
  }
}));

/**
 * Helper function to calculate stage progress
 * @param {Object} track - Current track data
 * @param {number} currentStage - Current stage number
 * @returns {number} Progress within stage (0-1)
 */
const calculateStageProgress = (track, currentStage) => {
  // This is a simplified version - would be more sophisticated in real app
  // based on actual stage distances and current position
  
  const overallProgress = track.completed;
  const stageWidth = 1 / 6; // 6 stages
  const stageStart = (currentStage - 1) * stageWidth;
  const stageEnd = currentStage * stageWidth;
  
  if (overallProgress < stageStart) return 0;
  if (overallProgress > stageEnd) return 1;
  
  return (overallProgress - stageStart) / stageWidth;
};

/**
 * Helper function to estimate the time the record boat would have taken
 * to reach the current completion percentage
 * @param {number} completion - Current completion (0-1)
 * @param {Object} recordData - Record boat data
 * @returns {number} Estimated time in hours
 */
const calculateRecordTimeToCurrentPoint = (completion, recordData) => {
  // Calculate total record time
  const totalRecordHours = recordData.stages.reduce(
    (total, stage) => total + stage.elapsed_time_hours, 
    0
  );
  
  // Simplification: assume linear progress for record boat
  return totalRecordHours * completion;
};

// Linea Rossa'nın VMG değerini hesapla - Kat edilen toplam mesafe / Geçen toplam süre
const calculateLineaRossaVMG = (track) => {
  // Track geçerli değilse, 0 döndür
  if (!track || track.lat === undefined || track.lon === undefined) {
    console.warn('calculateLineaRossaVMG: Geçersiz track verisi');
    return 0;
  }
  
  // 1. Sinop checkpoint koordinatları
  const sinopPosition = [42.11667, 34.94473];
  console.log('Sinop koordinatları:', sinopPosition);
  
  // 2. Linea Rossa'nın şu anki konumu
  const currentPosition = [track.lat, track.lon];
  console.log('Linea Rossa güncel konum:', currentPosition);
  
  // 3. Sinop ile şu anki konum arasındaki mesafeyi hesapla (deniz mili cinsinden)
  const distanceFromSinop = calculateDistance(currentPosition, sinopPosition);
  console.log('Sinop\'tan mesafe (NM):', distanceFromSinop.toFixed(2));
  
  // 4. Toplam kat edilen mesafe = Hopa-Sinop arası (295 NM) + Sinop'tan sonraki mesafe
  const etap1Distance = 295; // Hopa-Sinop arası mesafe (NM)
  const totalDistanceCovered = etap1Distance + distanceFromSinop;
  console.log('Toplam kat edilen mesafe:', 
              'Etap 1 (Hopa-Sinop):', etap1Distance, 'NM', 
              '+ Sinop sonrası:', distanceFromSinop.toFixed(2), 'NM', 
              '= Toplam:', totalDistanceCovered.toFixed(2), 'NM');
  
  // 5. Yarış başlangıç zamanı - 22 Mayıs 2025, 05:46 (GMT+3)
  const startDate = new Date(2025, 4, 22, 5, 46, 0);
  console.log('Başlangıç tarihi:', startDate.toLocaleString());
  
  // 6. Şu anki tarih ve saat
  const currentDate = new Date();
  console.log('Güncel tarih:', currentDate.toLocaleString());
  
  // 7. Geçen toplam süre (saat cinsinden)
  const totalElapsedHours = (currentDate - startDate) / (1000 * 60 * 60);
  const totalElapsedDays = totalElapsedHours / 24;
  console.log('Geçen süre:', 
              totalElapsedHours.toFixed(2), 'saat', 
              '=', totalElapsedDays.toFixed(2), 'gün', 
              '=', (totalElapsedHours * 60).toFixed(1), 'dakika');
  
  // 8. VMG = Toplam Mesafe / Toplam Geçen Süre (knot = deniz mili / saat)
  const vmg = totalDistanceCovered / totalElapsedHours;
  
  console.log('HESAPLAMA: VMG = Toplam Mesafe / Toplam Süre');
  console.log(`VMG = ${totalDistanceCovered.toFixed(2)} NM / ${totalElapsedHours.toFixed(2)} saat = ${vmg.toFixed(2)} knot`);
  
  return vmg;
};

// Linea Rossa'nın etap bilgilerini güncelle
const updateLineaRossaStages = (track) => {
  const { checkpointsReached, lineaRossaStages, recordBoat } = useRaceStore.getState();
  
  // Mevcut konum
  const currentPosition = [track.lat, track.lon];
  
  // Etap noktaları
  const checkpoints = recordBoat.checkpoints;
  
  // Yeni ulaşılan checkpoint var mı kontrol et
  let newCheckpointsReached = [...checkpointsReached];
  let updatedStages = [...lineaRossaStages];
  
  // Her checkpoint için mesafe kontrolü yap
  for (let i = 0; i < checkpoints.length; i++) {
    const checkpoint = checkpoints[i];
    const checkpointPosition = [checkpoint.coordinates.lat, checkpoint.coordinates.lng];
    
    // Checkpoint'e olan mesafeyi hesapla (deniz mili cinsinden)
    const distance = calculateDistance(currentPosition, checkpointPosition);
    
    // Eğer checkpoint'e yeterince yaklaştıysak ve daha önce ulaşılmadıysa
    if (distance < 2 && !newCheckpointsReached.includes(checkpoint.id)) {
      // Checkpoint'e ulaşıldı olarak işaretle
      newCheckpointsReached.push(checkpoint.id);
      
      // Yarış başlangıç zamanı
      const startDate = new Date(2025, 4, 22, 5, 46, 0); // 22 Mayıs 2025, 05:46
      const currentDate = new Date();
      
      // Etap bilgisini oluştur
      if (i > 0 && i < checkpoints.length) {
        const prevCheckpoint = checkpoints[i-1];
        const stageIndex = i-1;
        
        // Etap mesafesi (recordBoatData'dan al)
        const stageDistance = recordBoat.stages[stageIndex].distance_nm;
        
        // Etap süresi
        let stageStartTime;
        
        // İlk etap için başlangıç zamanını kullan
        if (stageIndex === 0) {
          stageStartTime = startDate;
        } else {
          // Önceki etabın bitiş zamanını bul
          const prevStage = updatedStages.find(s => s.stageIndex === stageIndex - 1);
          stageStartTime = prevStage ? prevStage.endTime : startDate;
        }
        
        // Etap süresi (saat cinsinden)
        const stageElapsedHours = (currentDate - stageStartTime) / (1000 * 60 * 60);
        
        // Etap VMG'si
        const stageVMG = stageElapsedHours > 0 ? stageDistance / stageElapsedHours : 0;
        
        // Etap bilgisini ekle
        updatedStages.push({
          stageIndex,
          from: prevCheckpoint.name,
          to: checkpoint.name,
          distance: stageDistance,
          elapsedHours: stageElapsedHours,
          vmg: stageVMG,
          startTime: stageStartTime,
          endTime: currentDate
        });
      }
    }
  }
  
  // Checkpoint listesini güncelle
  if (newCheckpointsReached.length > checkpointsReached.length) {
    useRaceStore.setState({ checkpointsReached: newCheckpointsReached });
  }
  
  return updatedStages;
};

// İki nokta arasındaki mesafeyi hesapla (deniz mili cinsinden)
const calculateDistance = (point1, point2) => {
  const R = 6371; // Dünya yarıçapı (km)
  const lat1 = point1[0] * Math.PI / 180;
  const lat2 = point2[0] * Math.PI / 180;
  const deltaLat = (point2[0] - point1[0]) * Math.PI / 180;
  const deltaLon = (point2[1] - point1[1]) * Math.PI / 180;
  
  const a = Math.sin(deltaLat/2) * Math.sin(deltaLat/2) +
          Math.cos(lat1) * Math.cos(lat2) *
          Math.sin(deltaLon/2) * Math.sin(deltaLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c;
  
  // Kilometre -> Deniz mili dönüşümü (1 km = 0.539957 deniz mili)
  return distance * 0.539957;
};

export default useRaceStore;
